<template>
    <div ref="coordModal" class="coord-modal">
        <a-button @click="copyCoords" type="text" :title="`${coords.lat}, ${coords.lon}`" style="width:100%">Copiar coordenadas</a-button>
        <template v-if="canRegisterAttachments">
            <a-divider style="margin:5px" />
            <a-button @click="openDocuments" :icon="h(FileOutlined)" type="text" class="center-flex" style="width:100%">
                Adicionar Documento ({{ fileCounts.documents }})
            </a-button>
            <a-button @click="openImages" :icon="h(FileImageOutlined)" type="text" class="center-flex" style="width:100%">
                Adicionar Imagem ({{ fileCounts.images }})
            </a-button>
        </template>
        <template v-if="canBulkUpload">
            <a-divider style="margin:5px" />
            <a-button @click="openBulkUpload" :icon="h(CloudUploadOutlined)" type="text" class="center-flex" style="width:100%">
                Upload em massa
            </a-button>
        </template>
    </div>

    <LayerFiles
        ref="layerFilesRef"
        :coords="coords"
        :layers-available="layersAvailable"
        :geometry-hits="geometryHits"
        @reload-files="reloadFiles"
        @files-loaded="onFilesLoaded"
    />
    <LayerBulkFiles
        ref="layerBulkFilesRef"
        :layers-available="bulkLayersAvailable"
        @reload-files="reloadFiles"
    />
</template>

<script setup>
import { toLonLat } from 'ol/proj'
import { computed, nextTick, onMounted, ref, h } from 'vue'
import { notification } from 'ant-design-vue'
import { FileImageOutlined, FileOutlined, CloudUploadOutlined } from '@ant-design/icons-vue'
import verifySpecificFuncPermissions from '../../../../utils/verifySpecificFuncPermissions'
import LayerFiles from './LayerFiles.vue'
import LayerBulkFiles from './LayerBulkFiles.vue'
import { mapLayerToBulkOption, plainMapLayers } from '../utils/mapCadastralLayerHelpers'

/** Funcionalidade específica "Anexos no mapa" (SpecificFunctionalitiesSeeder id 4). */
const SPECIFIC_FUNC_ANEXOS_MAPA = 4
/** Funcionalidade específica "Upload em Massa" (SpecificFunctionalitiesSeeder id 6). */
const SPECIFIC_FUNC_UPLOAD_MASSA = 6

const emit = defineEmits(['reloadFiles'])
const props = defineProps(['map', 'disablePermissionCheck'])

const coordModal = ref('')
const layerFilesRef = ref(null)
const layerBulkFilesRef = ref(null)
const coords = ref({ lon: '', lat: '' })
const layersAvailable = ref([])
const bulkLayersAvailable = ref([])
const geometryHits = ref([])
const fileCounts = ref({ documents: 0, images: 0 })
const mapAttachmentsPermission = ref(false)
const bulkUploadPermission = ref(false)

const canRegisterAttachments = computed(() => (
    mapAttachmentsPermission.value
    && Array.isArray(layersAvailable.value)
    && layersAvailable.value.length > 0
))

const canBulkUpload = computed(() => (
    bulkUploadPermission.value
    && Array.isArray(bulkLayersAvailable.value)
    && bulkLayersAvailable.value.length > 0
))

const canQueryLayerGeometry = computed(() => (
    mapAttachmentsPermission.value || bulkUploadPermission.value
))

onMounted(async () => {
    if (props.disablePermissionCheck) {
        mapAttachmentsPermission.value = false
        bulkUploadPermission.value = false
    } else {
        try {
            const perms = await verifySpecificFuncPermissions()
            mapAttachmentsPermission.value = !!(perms?.[SPECIFIC_FUNC_ANEXOS_MAPA] ?? perms?.[String(SPECIFIC_FUNC_ANEXOS_MAPA)])
            bulkUploadPermission.value = !!(perms?.[SPECIFIC_FUNC_UPLOAD_MASSA] ?? perms?.[String(SPECIFIC_FUNC_UPLOAD_MASSA)])
        } catch {
            mapAttachmentsPermission.value = false
            bulkUploadPermission.value = false
        }
    }

    props.map.getViewport().addEventListener('contextmenu', async (evt) => {
        evt.preventDefault()

        const allLayers = plainMapLayers(props.map)
            .reverse()
            .map(layer => {
                const option = mapLayerToBulkOption(layer)
                return {
                    ...option,
                    available: layer.getVisible() && layer.get('displayInLayerSwitcher') !== false,
                }
            })

        const candidateLayers = allLayers.filter(layer => layer.available && layer.value !== '')
        const cadastralCandidateLayers = candidateLayers.filter(layer => layer.isCadastral)

        const lonLat = toLonLat(
            props.map.getEventCoordinate(evt),
            props.map.getView().getProjection()
        )

        coords.value = { lon: lonLat[0].toFixed(6), lat: lonLat[1].toFixed(6) }
        coordModal.value.style.display = 'block'

        if (evt.clientX + coordModal.value.offsetWidth > window.innerWidth) {
            coordModal.value.style.left = `${evt.clientX - coordModal.value.offsetWidth - 20}px`
        } else {
            coordModal.value.style.left = `${evt.clientX}px`
        }
        coordModal.value.style.top = `${evt.clientY}px`

        layersAvailable.value = []
        bulkLayersAvailable.value = []
        geometryHits.value = []
        fileCounts.value = { documents: 0, images: 0 }

        if (candidateLayers.length === 0) return
        if (!canQueryLayerGeometry.value) return

        try {
            const { data } = await window.axios.post('/api/map-actions/layers/geometry-at-point', {
                lon: Number(lonLat[0]),
                lat: Number(lonLat[1]),
                layers: candidateLayers.map(l => ({ id: String(l.value) })),
            })
            const hits = data?.data?.layers ?? []
            geometryHits.value = hits
            const hitIdSet = new Set(hits.map(h => String(h.layer_id)))
            layersAvailable.value = candidateLayers.filter(l => hitIdSet.has(String(l.value)))
            bulkLayersAvailable.value = cadastralCandidateLayers.filter(l => hitIdSet.has(String(l.value)))

            if (mapAttachmentsPermission.value && layersAvailable.value.length > 0) {
                await nextTick()
                await layerFilesRef.value?.loadFiles()
            }
        } catch (e) {
            console.warn('geometry-at-point falhou:', e)
            layersAvailable.value = []
            bulkLayersAvailable.value = []
            geometryHits.value = []
        }
    })

    window.addEventListener('click', () => {
        coordModal.value.style.display = 'none'
    })
})

const copyCoords = () => {
    navigator.clipboard.writeText(`${coords.value.lat}, ${coords.value.lon}`)
    notification.success({ description: 'Coordenada copiada com sucesso!' })
}

const openDocuments = () => layerFilesRef.value?.openArchive('documentos')
const openImages = () => layerFilesRef.value?.openArchive('imagens')
const openBulkUpload = () => layerBulkFilesRef.value?.openBulkUpload()
const reloadFiles = () => emit('reloadFiles')
const onFilesLoaded = (counts) => {
    fileCounts.value = counts
}
</script>

<style scoped>
.coord-modal {
    display: none;
    padding: 5px 10px;
    border-radius: 8px;
    background-color: #fff;
    position: absolute;
    transform: translate(10px, -20px);
    z-index: 1000;
}

.center-flex {
    display: flex;
    justify-content: start;
    align-items: center;
}
</style>
